import { useState } from "react";
import { Download, FileText, Shield } from "lucide-react";
import { useDocuments, type DocumentItem } from "@/shared/api/wb";
import { Button, Card, EmptyState, Segmented } from "@/shared/ui";
import { fmtRubFull } from "@/shared/lib/format";

const KIND_RU: Record<string, string> = {
  act:         "Акт",
  invoice:     "Счёт",
  erir_report: "Отчёт",
  contract:    "Договор",
  erid_token:  "ОРД",
};

const STATUS_PILL: Record<string, { bg: string; ink: string }> = {
  "Подписан":  { bg: "var(--status-agree-bg)", ink: "var(--status-agree-ink)" },
  "Оплачен":   { bg: "var(--status-pub-bg)",   ink: "var(--status-pub-ink)" },
  "Автоотчёт": { bg: "var(--status-results-bg)", ink: "var(--status-results-ink)" },
  "Действует": { bg: "var(--status-pub-bg)",   ink: "var(--status-pub-ink)" },
  "В ЕРИР":    { bg: "var(--status-pub-bg)",   ink: "var(--status-pub-ink)" },
};

export function DocumentsPage() {
  const { data } = useDocuments();
  const [tab, setTab] = useState<"ord" | "closing">("ord");
  const docs = data ?? [];
  const ord = docs.filter((d) => d.kind === "erid_token");
  const closing = docs.filter((d) => d.kind !== "erid_token");

  return (
    <div className="px-7 pt-6 pb-14 max-w-[1100px] mx-auto">
      <div className="text-[11px] font-display font-semibold uppercase tracking-[0.16em] text-ink-40 mb-2">Маркировка · закрывающие · ЕРИР</div>
      <h1 className="font-display font-extrabold text-[28px] tracking-tight">Документы</h1>
      <p className="text-[13px] text-ink-60 mt-2 mb-5 max-w-[560px] leading-relaxed">
        Маркировку ОРД и отчётность в ЕРИР делаем автоматически. Все токены, акты и счета — здесь.
      </p>

      {docs.length === 0 ? (
        <EmptyState
          icon={<FileText size={28} strokeWidth={1.8} />}
          title="Документы появятся после первой РК"
          description={<>
            На каждый креатив выпустим <b>erid</b>-токен, оформим акты и счета и сами отправим
            данные в ЕРИР. Здесь будете скачивать готовое.
          </>}
          primaryCta={{ label: "Запустить РК", to: "/campaign/new" }}
        />
      ) : (
        <>
          <Segmented<"ord" | "closing"> value={tab} onChange={setTab}
            options={[
              { value: "ord", label: `Маркировка ОРД · ${ord.length}` },
              { value: "closing", label: `Закрывающие · ${closing.length}` },
            ]}
          />

          <div className="mt-5">
            {tab === "ord" ? <OrdList rows={ord} /> : <ClosingList rows={closing} />}
          </div>
        </>
      )}
    </div>
  );
}

function OrdList({ rows }: { rows: DocumentItem[] }) {
  return (
    <div className="grid gap-2.5">
      <Card className="!p-4 flex items-start gap-3.5">
        <div className="w-9 h-9 rounded-md bg-lime grid place-items-center text-[var(--fg-on-accent)] flex-shrink-0">
          <Shield size={16} />
        </div>
        <div className="text-[13px] text-ink-60 leading-relaxed">
          <b>1 креатив = 1 токен erid.</b> Категория и реквизиты подтянуты автоматически — отправка в ЕРИР происходит сама.
        </div>
      </Card>
      {rows.map((d) => (
        <Card key={d.id} className="!p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-purple-900/10 text-purple-900 grid place-items-center flex-shrink-0"><Shield size={18} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-[14px] mb-0.5 truncate">{d.title}</div>
            <div className="flex items-center gap-2.5 flex-wrap">
              {d.erid && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm bg-purple-900/10 text-purple-900 text-[11.5px] font-mono font-bold">
                  erid: {d.erid}
                </span>
              )}
              <span className="text-[12px] text-ink-40">Дружеский · в 5 размещениях</span>
            </div>
          </div>
          <PillRu label="В ЕРИР" />
          <Button size="sm" variant="outline"><Download size={12} /> Акт</Button>
        </Card>
      ))}
    </div>
  );
}

function ClosingList({ rows }: { rows: DocumentItem[] }) {
  return (
    <div className="grid gap-2.5">
      {rows.map((d) => (
        <Card key={d.id} className="!p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-cream grid place-items-center flex-shrink-0"><FileText size={18} /></div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-semibold text-[14px] mb-0.5 truncate">{d.title}</div>
            <div className="text-[12px] text-ink-40">{KIND_RU[d.kind] ?? d.kind}</div>
          </div>
          {d.amount_rub !== null && (
            <div className="font-mono font-bold text-[14px] text-right">{fmtRubFull(d.amount_rub)}</div>
          )}
          <PillRu label={d.status} />
          <Button size="sm" variant="outline"><Download size={12} /></Button>
        </Card>
      ))}
    </div>
  );
}

function PillRu({ label }: { label: string }) {
  const colors = STATUS_PILL[label] ?? { bg: "var(--status-draft-bg)", ink: "var(--status-draft-ink)" };
  const isOrd = label === "В ЕРИР";
  return (
    <span className="px-2.5 py-1 rounded-pill text-[11px] font-display font-semibold uppercase tracking-wider"
      style={{ background: colors.bg, color: colors.ink }}>
      {isOrd ? "✓ В ЕРИР" : label}
    </span>
  );
}
